/**
 * MetricInfo — hover/focus/tap "what does this number mean?" explainer.
 *
 * Lane 38 (Number Explainability) build — Commit 2.
 * Design: docs/audits/2026-07-01_number-explainability_design.md §1, §2.
 *
 * Wraps a number the caller already renders (children) and appends a small info glyph.
 * DESCRIPTION ONLY (Lane 142 introduced two tiers; QA R2-B / Mega QA 2 2026-07-22
 * trimmed to one):
 *   hover/focus/tap → name + ONE plain sentence (the entry's `short`, falling back
 *   to its definition). The old "How this is computed" expansion (definition →
 *   method → the look-alike cross-reference) was removed. Nothing was deleted from
 *   the registry — howComputed / crossReference / definition still live in
 *   metricExplanations.json; they just stopped being rendered (render change only).
 *
 * FABRICATION FIREWALL (§2.3): the prose is 100% static + human-written and lives in
 * frontend/src/data/metricExplanations.json. The ONLY live values that enter are
 * explicitly-declared, numeric-only slots. No model output ever reaches the popover.
 *
 * Fold-ins folded in here:
 *   C1 — if the registry is missing/wrong-shape at runtime, degrade to NO glyphs
 *        (the page still renders); warn once. The explainer never takes a page down.
 *   C2 — a declared slot whose value is null/undefined/NaN never renders "null%" or a
 *        raw {token}. Lane 1387 (Ryan): it no longer renders "Not available" in the
 *        middle of a sentence either ("Covers Not available."); the sentence that states
 *        the missing value is left out of the popover (interpolate).
 *   D1 — the glyph is an "i"-in-circle SVG meeting >=3:1 non-text contrast AT REST.
 *   D2 — in-popover hierarchy: name label (small, muted) over the one-sentence description.
 *   D3 — the popover never occludes its own trigger number (placed above, flips below only
 *        when there isn't room above).
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatValue } from '../utils/formatValue';
import registryFile from '../data/metricExplanations.json';

// ── Registry access, fail-silent (fold-in C1) ───────────────────────────────
// A static JSON import is validated at BUILD time, so a truly malformed file fails the
// build (the safest failure). This guard additionally tolerates an unexpected SHAPE at
// runtime: if `metrics` isn't an object, the whole explainer disappears (no glyphs) and
// we warn once, rather than throwing and taking the page down.
let REGISTRY = {};
let REGISTRY_OK = true;
try {
  if (
    registryFile &&
    typeof registryFile === 'object' &&
    registryFile.metrics &&
    typeof registryFile.metrics === 'object'
  ) {
    REGISTRY = registryFile.metrics;
  } else {
    REGISTRY_OK = false;
  }
} catch (e) {
  REGISTRY_OK = false;
}
if (!REGISTRY_OK && typeof console !== 'undefined') {
  console.warn(
    '[METRIC-INFO-REGISTRY-INVALID] metricExplanations.json missing or wrong shape; number explainers disabled (page unaffected)'
  );
}

const _warnedKeys = new Set();

/** Look up an entry; warn once per unknown key (dev aid), never throw. */
export function getEntry(metricKey) {
  if (!REGISTRY_OK) return undefined;
  const entry = REGISTRY[metricKey];
  if (!entry && !_warnedKeys.has(metricKey) && typeof console !== 'undefined') {
    _warnedKeys.add(metricKey);
    console.warn(`[METRIC-INFO-MISSING-KEY] no registry entry for metricKey="${metricKey}"`);
  }
  return entry;
}

const _TOKEN_RE = /\{([a-zA-Z0-9_]+)\}/g;

// The sentences of a template, each with its own end mark and the space after it, so
// joining the ones kept gives the text back unchanged. A full stop only ends a sentence
// when a space or the end of the text follows it ("1.5x" stays one sentence).
function sentences(text) {
  const out = [];
  const end = /[.!?]+(\s+|$)/g;
  let start = 0;
  let m;
  while ((m = end.exec(text)) !== null) {
    out.push(text.slice(start, m.index + m[0].length));
    start = m.index + m[0].length;
    if (start >= text.length) break;
  }
  if (start < text.length) out.push(text.slice(start));
  return out;
}

/**
 * Interpolate declared numeric slots into a static template string. THE FIREWALL.
 *   - Only tokens whose name is a DECLARED slot are filled (allowlist).
 *   - A value supplied for an UNDECLARED key is dropped + dev-warned.
 *   - A sentence that holds a declared slot whose value is null/undefined/NaN is LEFT
 *     OUT (Lane 1387, Ryan: "Covers Not available." does not read naturally). The page
 *     already shows the figure itself as missing; the popover only explains it.
 *   - Any leftover {token} (undeclared or unfilled) is STRIPPED, never shown raw.
 *   - Pure string substitution — never eval, never template execution.
 */
export function interpolate(text, slots, values) {
  if (typeof text !== 'string' || text.length === 0) return text || '';
  const declared = new Map((slots || []).map((s) => [s.name, s]));

  // Warn on undeclared supplied keys (they are dropped — allowlist discipline).
  if (values && typeof values === 'object' && typeof console !== 'undefined') {
    for (const k of Object.keys(values)) {
      if (!declared.has(k)) {
        console.warn(`[METRIC-INFO-UNDECLARED-SLOT] value "${k}" is not a declared slot; dropped`);
      }
    }
  }

  // Leave out every sentence that states a declared value we do not have (Lane 1387).
  const missing = (name) => {
    if (!declared.has(name)) return false;
    const raw = values ? values[name] : undefined;
    return raw === null || raw === undefined || Number.isNaN(raw);
  };
  const kept = sentences(text).filter((s) => {
    const names = [...s.matchAll(_TOKEN_RE)].map((m) => m[1]);
    return !names.some(missing);
  });

  // Fill declared tokens.
  let out = kept.join('').trim().replace(_TOKEN_RE, (m, name) => {
    const slot = declared.get(name);
    if (!slot) return m; // leave undeclared tokens for the leftover sweep
    const raw = values ? values[name] : undefined;
    return formatValue(raw, slot.type);
  });

  // Leftover sweep: strip any remaining {token}, never render it raw.
  out = out.replace(_TOKEN_RE, (m, name) => {
    if (typeof console !== 'undefined') {
      console.warn(`[METRIC-INFO-LEFTOVER-TOKEN] token {${name}} not resolved; stripped`);
    }
    return '';
  });
  return out;
}

// ── The "i"-in-circle glyph (fold-in D1). No emoji (design-system rule). ─────
// Linear rebuild Stage 2 (Lane 219): the glyph draws in `currentColor`, so it
// inherits the trigger button's CSS `color` (a Linear var() that resolves inside
// the `.sd`/`.an` page scope this component always renders in). A bare SVG
// stroke/fill ATTRIBUTE cannot resolve var(), so currentColor is the bridge.
function InfoGlyph() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block' }}
    >
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="8" cy="4.6" r="0.95" fill="currentColor" />
      <rect x="7.15" y="6.6" width="1.7" height="5" rx="0.85" fill="currentColor" />
    </svg>
  );
}

// Linear muted-icon (rest) and accent (hover) tokens, set as the button's CSS
// `color` where var() resolves. At rest --text-3 (#8d919b) is ~3.16:1 on the
// near-white Linear panel — clears the D1 >=3:1 non-text-contrast floor.
const GLYPH_REST = 'var(--text-3)';
const GLYPH_HOVER = 'var(--accent)';

/**
 * MetricInfo props:
 *   - metricKey (string, required) — registry lookup key.
 *   - values (object, optional)    — live values for declared numeric slots.
 *   - children                     — the already-rendered number (rendered verbatim).
 */
export default function MetricInfo({ metricKey, values, children }) {
  const entry = getEntry(metricKey);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Interaction model (Lane 142; trimmed QA R2-B, Mega QA 2 2026-07-22): HOVER TO
  // PEEK, CLICK TO PIN. The popover now shows the DESCRIPTION ONLY (the entry's
  // one-sentence `short`, falling back to its definition). The old two-tier
  // "How this is computed" expansion — method + look-alike cross-reference — was
  // cut. The registry still carries howComputed / crossReference / definition;
  // they are simply no longer rendered (render change, registry data unchanged).
  //   hover  -> the popover peeks open with the description.
  //   click  -> PINS it open (so a tap on touch keeps it up; the pin survives
  //             mouseleave). Clicking the glyph again closes.
  //   close  -> ESC, click-outside, or the second glyph click.
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState(null); // null until the portaled card is measured
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const cardRef = useRef(null); // the PORTALED popover card (outside wrapRef's subtree)
  const pinnedRef = useRef(false);
  pinnedRef.current = pinned;

  // Grace-delay close: the card is portaled, so the mouse briefly leaves the wrap on its
  // way into the popover — an immediate close makes the card unreachable by mouse
  // (caught live in the Lane 142 preview). 140ms bridges the travel.
  const closeTimer = useRef(null);
  const cancelScheduledClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimer.current = setTimeout(() => {
      if (!pinnedRef.current) setOpen(false);
    }, 140);
  };
  useEffect(() => cancelScheduledClose, []); // clear the timer on unmount

  // Closing the popover always unpins it.
  useEffect(() => {
    if (!open) {
      setPinned(false);
    }
  }, [open]);

  // ESC + click-outside + scroll close (the card lives in a portal — check both trees).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inCard = cardRef.current && cardRef.current.contains(e.target);
      if (!inWrap && !inCard) setOpen(false);
    };
    const onScroll = () => setOpen(false); // fixed-position card would drift — close instead
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // Placement (D3: prefer ABOVE so the popover never covers the number; go below when
  // there isn't room above; clamp fully on-screen with internal scroll as last resort).
  // Fixed viewport coordinates + a PORTAL to document.body, because the old in-place
  // absolute positioning was CLIPPED INVISIBLE by any overflow:auto ancestor (the
  // dossier YoY table's scroll container — caught live in the Lane 142 preview).
  // Two-pass: the card renders hidden, we MEASURE it, then position + reveal — so the
  // measured card never pokes off the top of the viewport (also caught live).
  useEffect(() => {
    if (!open) {
      setPos(null); // back to hidden-until-measured on next open
      return;
    }
    if (!triggerRef.current || !cardRef.current) return;
    try {
      const r = triggerRef.current.getBoundingClientRect();
      const cardH = cardRef.current.getBoundingClientRect().height || 0;
      const vw = window.innerWidth || 1024;
      const vh = window.innerHeight || 768;
      const left = Math.max(8, Math.min(r.left, vw - 328)); // keep the 320px card on-screen
      const topAbove = r.top - 6 - cardH;
      let top;
      let maxHeight = null;
      if (topAbove >= 8) {
        top = topAbove; // preferred: above the number
      } else if (r.bottom + 6 + cardH <= vh - 8) {
        top = r.bottom + 6; // fall back: below
      } else {
        top = 8; // neither fits whole: pin to top, scroll inside
        maxHeight = vh - 16;
      }
      setPos({ left, top, maxHeight });
    } catch (e) {
      setPos({ left: 8, top: 8, maxHeight: null });
    }
  }, [open]);

  // Fail-silent: missing entry OR invalid registry -> render the number unchanged, no glyph.
  if (!entry) return <>{children}</>;

  // The popover body: the one-sentence `short` description. An entry that
  // predates the field degrades to its definition — never empty. (QA R2-B: the
  // definition / howComputed / crossReference are retained in the registry but
  // no longer rendered.)
  // When every sentence of `short` states a value we do not have, the definition says
  // what the figure is instead (Lane 1387).
  const short = interpolate(entry.short || entry.definition, entry.slots, values)
    || interpolate(entry.definition, entry.slots, values);

  const glyphColor = hovered || open ? GLYPH_HOVER : GLYPH_REST;

  // The visible card, PORTALED to document.body at fixed viewport coordinates —
  // immune to ancestor overflow clipping and to inherited th styles.
  const cardStyle = {
    position: 'fixed',
    zIndex: 1000,
    left: pos ? pos.left : 0,
    top: pos ? pos.top : 0,
    visibility: pos ? 'visible' : 'hidden', // hidden until measured (two-pass placement)
    maxHeight: pos && pos.maxHeight ? pos.maxHeight : undefined,
    overflowY: pos && pos.maxHeight ? 'auto' : undefined,
    width: 'max-content',
    maxWidth: '320px',
    display: 'block',
    whiteSpace: 'normal',
    textAlign: 'left',
    // Portaled to body, but keep the resets explicit — prose must never render
    // ALL-CAPS (the th textTransform inheritance caught live in the Lane 142 preview).
    textTransform: 'none',
    letterSpacing: 'normal',
    fontSize: '13px',
    // Linear rebuild Stage 2 (Lane 219): the card is PORTALED to document.body, so
    // it sits OUTSIDE the `.sd`/`.an` page scope and cannot inherit the Linear
    // tokens. It reads them by NAME with a Linear-LIGHT fallback — light today, and
    // auto-dark the moment a future lane lifts these tokens to :root (which the
    // body-level portal WOULD inherit). Same reasoning Lane 215 used for the
    // recharts stroke attributes.
    background: 'var(--panel, #ffffff)',
    border: '1px solid var(--border, #e9eaee)',
    borderRadius: '8px',
    boxShadow: '0 6px 20px rgba(16, 18, 26, 0.14), 0 1px 3px rgba(16, 18, 26, 0.08)',
    padding: '10px 12px',
    fontWeight: 400,
    cursor: 'default',
  };

  return (
    <span
      ref={wrapRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'baseline', gap: '3px' }}
      onMouseEnter={() => {
        setHovered(true);
        cancelScheduledClose();
        setOpen(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
        scheduleClose(); // grace delay — the card cancels it on mouseenter; pins ignore it
      }}
    >
      {children}
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Explain ${entry.humanName}`}
        aria-expanded={open}
        title={short} /* native fallback (§1.5): hover yields the short even if the popover JS fails */
        onClick={() => {
          if (pinned) {
            setOpen(false); // second click closes (useEffect unpins)
          } else {
            setOpen(true); // click = pin (so a tap on touch keeps it up)
            setPinned(true);
          }
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          // Keep the popover open when focus moves INTO it (the card is portaled — check
          // both trees), and never blur-close a pin.
          if (pinned) return;
          const toWrap = wrapRef.current && wrapRef.current.contains(e.relatedTarget);
          const toCard = cardRef.current && cardRef.current.contains(e.relatedTarget);
          if (!toWrap && !toCard) setOpen(false);
        }}
        style={{
          appearance: 'none',
          border: 'none',
          background: 'transparent',
          padding: 0,
          margin: 0,
          cursor: 'help',
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 0,
          color: glyphColor,
        }}
      >
        <InfoGlyph />
      </button>

      {open && createPortal(
        <span
          ref={cardRef}
          role="tooltip"
          style={cardStyle}
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
        >
          {/* Name (small label) -> the one-sentence description. QA R2-B (Mega QA
              2 2026-07-22): the "How this is computed" expansion (method +
              look-alike cross-reference) was removed — description only.
              Linear label tier: 11px/600 uppercase, muted --text-3. */}
          <span
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-3, #8d919b)',
              marginBottom: '4px',
            }}
          >
            {entry.humanName}
          </span>
          <span style={{ display: 'block', fontSize: '13px', color: 'var(--text, #16171b)', lineHeight: 1.45 }}>
            {short}
          </span>
        </span>,
        document.body
      )}
    </span>
  );
}
